import type { ReactNode } from "react";

/** Stroke icons drawn in `currentColor`, so they follow the button's colour and the theme. Paths from Lucide (ISC). */

interface IconProps {
	size?: number;
	className?: string;
}

function Svg({ size = 16, className, children }: IconProps & { children: ReactNode }) {
	return (
		<svg
			className={className ? `svg-icon ${className}` : "svg-icon"}
			width={size}
			height={size}
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth={1.8}
			strokeLinecap="round"
			strokeLinejoin="round"
			aria-hidden="true"
			focusable="false"
		>
			{children}
		</svg>
	);
}

const FOLDER = "M20 20a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2Z";

export function FolderIcon(props: IconProps) {
	return (
		<Svg {...props}>
			<path d={FOLDER} />
		</Svg>
	);
}

export function FolderPlusIcon(props: IconProps) {
	return (
		<Svg {...props}>
			<path d={FOLDER} />
			<path d="M12 10v6" />
			<path d="M9 13h6" />
		</Svg>
	);
}

export function PlusIcon(props: IconProps) {
	return (
		<Svg {...props}>
			<path d="M5 12h14" />
			<path d="M12 5v14" />
		</Svg>
	);
}

/** An anonymous conversation: nothing here is read into memory or kept from it. */
export function EyeOffIcon(props: IconProps) {
	return (
		<Svg {...props}>
			<path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a18.5 18.5 0 0 1-2.16 3.19" />
			<path d="M6.61 6.61A18.15 18.15 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61" />
			<path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
			<path d="m2 2 20 20" />
		</Svg>
	);
}

/** A profile pinned to the top of the sidebar. Outlined; the stylesheet fills it while pressed. */
export function StarIcon(props: IconProps) {
	return (
		<Svg {...props}>
			<polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
		</Svg>
	);
}

export function PencilIcon(props: IconProps) {
	return (
		<Svg {...props}>
			<path d="M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z" />
			<path d="m15 5 4 4" />
		</Svg>
	);
}

/** The category a profile is filed under in the sidebar. */
export function TagIcon(props: IconProps) {
	return (
		<Svg {...props}>
			<path d="M12.586 2.586A2 2 0 0 0 11.172 2H4a2 2 0 0 0-2 2v7.172a2 2 0 0 0 .586 1.414l8.704 8.704a2.426 2.426 0 0 0 3.42 0l6.58-6.58a2.426 2.426 0 0 0 0-3.42z" />
			<circle cx="7.5" cy="7.5" r=".5" fill="currentColor" />
		</Svg>
	);
}

export function ListChecksIcon(props: IconProps) {
	return (
		<Svg {...props}>
			<path d="m3 17 2 2 4-4" />
			<path d="m3 7 2 2 4-4" />
			<path d="M13 6h8" />
			<path d="M13 12h8" />
			<path d="M13 18h8" />
		</Svg>
	);
}

export function CpuIcon(props: IconProps) {
	return (
		<Svg {...props}>
			<rect width="16" height="16" x="4" y="4" rx="2" />
			<rect width="6" height="6" x="9" y="9" rx="1" />
			<path d="M15 2v2" />
			<path d="M15 20v2" />
			<path d="M2 15h2" />
			<path d="M2 9h2" />
			<path d="M20 15h2" />
			<path d="M20 9h2" />
			<path d="M9 2v2" />
			<path d="M9 20v2" />
		</Svg>
	);
}

export function MenuIcon(props: IconProps) {
	return (
		<Svg {...props}>
			<path d="M4 6h16" />
			<path d="M4 12h16" />
			<path d="M4 18h16" />
		</Svg>
	);
}

export function ChevronLeftIcon(props: IconProps) {
	return (
		<Svg {...props}>
			<path d="m15 18-6-6 6-6" />
		</Svg>
	);
}

export function ChevronRightIcon(props: IconProps) {
	return (
		<Svg {...props}>
			<path d="m9 18 6-6-6-6" />
		</Svg>
	);
}

export function GearIcon(props: IconProps) {
	return (
		<Svg {...props}>
			<path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
			<circle cx="12" cy="12" r="3" />
		</Svg>
	);
}

export function SendIcon(props: IconProps) {
	return (
		<Svg {...props}>
			<path d="M3.7 20.3 21 12 3.7 3.7 5.9 12z" fill="currentColor" />
			<path d="M5.9 12H12" />
		</Svg>
	);
}

export function XIcon(props: IconProps) {
	return (
		<Svg {...props}>
			<path d="M18 6 6 18" />
			<path d="m6 6 12 12" />
		</Svg>
	);
}

export function ImageIcon(props: IconProps) {
	return (
		<Svg {...props}>
			<rect width="18" height="18" x="3" y="3" rx="3" />
			<circle cx="9" cy="9" r="2" />
			<path d="m21 15-5-5L5 21" />
		</Svg>
	);
}

export function FileIcon(props: IconProps) {
	return (
		<Svg {...props}>
			<path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
			<path d="M14 3v6h6" />
		</Svg>
	);
}

/** A workflow command. */
export function ZapIcon(props: IconProps) {
	return (
		<Svg {...props}>
			<path d="M13 2 4 14h7l-1 8 9-12h-7z" />
		</Svg>
	);
}

/** A script command. */
export function TerminalIcon(props: IconProps) {
	return (
		<Svg {...props}>
			<path d="m4 17 6-5-6-5" />
			<path d="M12 19h8" />
		</Svg>
	);
}

/** A workflow with no command, filled into the input as a slash command. */
export function SlashIcon(props: IconProps) {
	return (
		<Svg {...props}>
			<path d="M7 20 17 4" />
		</Svg>
	);
}

export function ChatIcon(props: IconProps) {
	return (
		<Svg {...props}>
			<path d="M7.9 20A9 9 0 1 0 4 16.1L2 22z" />
		</Svg>
	);
}

export function LoopIcon(props: IconProps) {
	return (
		<Svg {...props}>
			<path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
			<path d="M21 3v5h-5" />
			<path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
			<path d="M8 16H3v5" />
		</Svg>
	);
}

export function BrainIcon(props: IconProps) {
	return (
		<Svg {...props}>
			<path d="M12 5a3 3 0 1 0-5.997.125 4 4 0 0 0-2.526 5.77 4 4 0 0 0 .556 6.588A4 4 0 1 0 12 18Z" />
			<path d="M12 5a3 3 0 1 1 5.997.125 4 4 0 0 1 2.526 5.77 4 4 0 0 1-.556 6.588A4 4 0 1 1 12 18Z" />
			<path d="M12 5v13" />
		</Svg>
	);
}

export function NotebookIcon(props: IconProps) {
	return (
		<Svg {...props}>
			<path d="M2 6h4" />
			<path d="M2 10h4" />
			<path d="M2 14h4" />
			<path d="M2 18h4" />
			<rect width="16" height="20" x="4" y="2" rx="2" />
			<path d="M16 2v20" />
		</Svg>
	);
}

export function BookOpenIcon(props: IconProps) {
	return (
		<Svg {...props}>
			<path d="M12 7v14" />
			<path d="M3 18a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h5a4 4 0 0 1 4 4 4 4 0 0 1 4-4h5a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1h-6a3 3 0 0 0-3 3 3 3 0 0 0-3-3z" />
		</Svg>
	);
}

export function ClockIcon(props: IconProps) {
	return (
		<Svg {...props}>
			<circle cx="12" cy="12" r="9" />
			<path d="M12 7v5l3 2" />
		</Svg>
	);
}

export function WalletIcon(props: IconProps) {
	return (
		<Svg {...props}>
			<path d="M19 7V4a1 1 0 0 0-1-1H5a2 2 0 0 0 0 4h15a1 1 0 0 1 1 1v4h-3a2 2 0 0 0 0 4h3a1 1 0 0 0 1-1v-2a1 1 0 0 0-1-1" />
			<path d="M3 5v14a2 2 0 0 0 2 2h15a1 1 0 0 0 1-1v-4" />
		</Svg>
	);
}

export function SunIcon(props: IconProps) {
	return (
		<Svg {...props}>
			<circle cx="12" cy="12" r="4" />
			<path d="M12 2v2" />
			<path d="M12 20v2" />
			<path d="m4.93 4.93 1.41 1.41" />
			<path d="m17.66 17.66 1.41 1.41" />
			<path d="M2 12h2" />
			<path d="M20 12h2" />
			<path d="m6.34 17.66-1.41 1.41" />
			<path d="m19.07 4.93-1.41 1.41" />
		</Svg>
	);
}

export function MoonIcon(props: IconProps) {
	return (
		<Svg {...props}>
			<path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z" />
		</Svg>
	);
}

export function LogOutIcon(props: IconProps) {
	return (
		<Svg {...props}>
			<path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
			<path d="m16 17 5-5-5-5" />
			<path d="M21 12H9" />
		</Svg>
	);
}

export function UsersIcon(props: IconProps) {
	return (
		<Svg {...props}>
			<path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
			<circle cx="9" cy="7" r="4" />
			<path d="M22 21v-2a4 4 0 0 0-3-3.87" />
			<path d="M16 3.13a4 4 0 0 1 0 7.75" />
		</Svg>
	);
}

/** A conversation's settings, at the end of its header. */
export function MoreIcon(props: IconProps) {
	return (
		<Svg {...props}>
			<circle cx="12" cy="5" r="1" />
			<circle cx="12" cy="12" r="1" />
			<circle cx="12" cy="19" r="1" />
		</Svg>
	);
}

export function SquareIcon(props: IconProps) {
	return (
		<Svg {...props}>
			<rect width="12" height="12" x="6" y="6" rx="2" fill="currentColor" />
		</Svg>
	);
}

export function ChevronDownIcon(props: IconProps) {
	return (
		<Svg {...props}>
			<path d="m6 9 6 6 6-6" />
		</Svg>
	);
}

export function ArchiveIcon(props: IconProps) {
	return (
		<Svg {...props}>
			<rect width="20" height="5" x="2" y="3" rx="1" />
			<path d="M4 8v11a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8" />
			<path d="M10 12h4" />
		</Svg>
	);
}

export function SearchIcon(props: IconProps) {
	return (
		<Svg {...props}>
			<circle cx="11" cy="11" r="7" />
			<path d="m20 20-3.5-3.5" />
		</Svg>
	);
}

export function PlayIcon(props: IconProps) {
	return (
		<Svg {...props}>
			<path d="M7 4v16l13-8z" fill="currentColor" />
		</Svg>
	);
}

export function BellIcon(props: IconProps) {
	return (
		<Svg {...props}>
			<path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
			<path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
		</Svg>
	);
}

/** The Figma bridge. */
export function FigmaIcon(props: IconProps) {
	return (
		<Svg {...props}>
			<path d="M12 2H8.5a3.5 3.5 0 0 0 0 7H12z" />
			<path d="M12 9H8.5a3.5 3.5 0 0 0 0 7H12z" />
			<path d="M12 2h3.5a3.5 3.5 0 0 1 0 7H12z" />
			<circle cx="15.5" cy="12.5" r="3.5" />
			<path d="M8.5 16A3.5 3.5 0 1 0 12 19.5V16z" />
		</Svg>
	);
}
